import { api, ApiError, unwrap } from "@/shared/api/client";

export function localLogin(input: { email: string; password: string }) {
  return unwrap(
    api().POST("/api/v1/auth/login", {
      body: { email: input.email, password: input.password },
    }),
  );
}

export function listIdentityProviders(): Promise<{ id: string; name: string }[]> {
  return unwrap(api().GET("/api/v1/auth/idp")).then((providers) =>
    providers.filter((provider) => typeof provider.id === "string" && typeof provider.name === "string").map((provider) => ({ id: provider.id!, name: provider.name! })),
  );
}

/** Returns the IdP authorisation URL; the API stores the one-use state and PKCE verifier server-side. */
export async function beginSso(providerName: string): Promise<string> {
  const result = await unwrap(
    api().POST("/api/v1/auth/oidc/{provider_name}/begin", { params: { path: { provider_name: providerName } } }),
  );
  const url = result.authorization_url;
  if (typeof url !== "string") throw new ApiError("The identity provider did not return a sign-in address.");
  return url;
}

export function completeSso(input: { state: string; code: string }) {
  return unwrap(api().GET("/api/v1/auth/oidc/callback", { params: { query: input } }));
}

/** Only follow http(s) authorisation URLs (guards against javascript:/data: values). */
export function isNavigableUrl(url: string): boolean {
  try {
    return ["https:", "http:"].includes(new URL(url).protocol);
  } catch {
    return false;
  }
}

export type AuthFailure = "credentials" | "forbidden" | "maintenance" | "unavailable";

/** Local credential failures never reveal tenant or account status. */
export function classifyAuthError(error: unknown): AuthFailure {
  if (!(error instanceof ApiError)) return "unavailable";
  if (error.isMaintenance) return "maintenance";
  if (error.code === "invalid_credentials") return "credentials";
  switch (error.status) {
    case 401:
      return "credentials";
    case 403:
      return "forbidden";
    case 404:
      return "unavailable";
    default:
      return "unavailable";
  }
}
